import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  NEWS_PAGE_SIZE,
  queryNewsArticles,
  queryNewsCategories,
} from "@api/news";
import { ALL_CATEGORY, buildCategoryTabs } from "./news";

/** 续页骨架兜底数量：接口未返回 total 时使用（Web 4 / Mobile 3，由页面传入） */
const DEFAULT_MORE_SKELETON_FALLBACK = 4;

/**
 * 资讯列表的公共取数 / 分页逻辑 —— Web + Pad / Mobile 共用。
 *
 * 只负责「拉分类 → 拉列表 → 续页 → 失败重试」，触发续页的交互归页面自己：
 * Web 用底部哨兵（IntersectionObserver），移动端用下拉阈值手势，两端都调 `loadMore()`。
 * 这样两端的去重、防死循环、请求作废、错误态这些容易写歪的地方只有一份。
 *
 * @param {Object} [options]
 * @param {number} [options.moreSkeletonFallback=4] total 缺失时续页骨架的兜底数量
 */
export default function useNewsList({
  moreSkeletonFallback = DEFAULT_MORE_SKELETON_FALLBACK,
} = {}) {
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [activeCategory, setActiveCategory] = useState(ALL_CATEGORY.value);
  const [articles, setArticles] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [categoriesError, setCategoriesError] = useState(false);
  const [articlesError, setArticlesError] = useState(false);
  const [categoriesReady, setCategoriesReady] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  /* 已加载文章的快照：请求回调里做去重 / 判新增，避免闭包读到旧 state */
  const articlesRef = useRef([]);
  /* 下一次要请求的页码；只在成功且确有下一页时递增 */
  const nextPageRef = useRef(2);
  /* 请求序号：切分类 / 重试 / 卸载后丢弃在途响应 */
  const requestSeqRef = useRef(0);
  /* 续页同步锁：state 更新是异步的，同一帧内的重复触发要靠 ref 挡住 */
  const appendLoadingRef = useRef(false);

  const hasError = categoriesError || articlesError;

  const tabs = useMemo(
    () => buildCategoryTabs(categoryOptions),
    [categoryOptions]
  );

  /* 只有「全部」展示置顶标记，与 Web 一致 */
  const showPinned = activeCategory === ALL_CATEGORY.value;

  /* 续页骨架数 = 这一页实际还会来的条数；total 缺失时用页面传入的兜底值 */
  const remainingCount = Math.max(total - articles.length, 0);
  const moreSkeletonCount =
    remainingCount > 0
      ? Math.min(NEWS_PAGE_SIZE, remainingCount)
      : moreSkeletonFallback;

  /* 空态：无错误、首屏已加载完、且确实没有文章 */
  const showEmpty = !hasError && !listLoading && articles.length === 0;

  useEffect(() => {
    let alive = true;

    setCategoriesReady(false);

    queryNewsCategories()
      .then((res) => {
        if (!alive) return;

        if (!res?.success || !Array.isArray(res.result)) {
          setCategoryOptions([]);
          setCategoriesError(true);
          setCategoriesReady(false);
          return;
        }

        setCategoryOptions(res.result);
        setCategoriesError(false);
        setCategoriesReady(true);
      })
      .catch(() => {
        if (!alive) return;
        setCategoryOptions([]);
        setCategoriesError(true);
        setCategoriesReady(false);
      });

    return () => {
      alive = false;
    };
  }, [retryKey]);

  /**
   * 拉取指定页：
   * - append=false：首屏 / 切分类，先清空列表并展示骨架；
   * - append=true：续页，保留已有列表，尾部追加骨架；
   * - 任一页失败都进整页错误态（分类 Tab 一并隐藏），由「重新加载」从第 1 页重来。
   */
  const loadPage = useCallback(
    (page, { append }) => {
      if (append) {
        if (appendLoadingRef.current) return;
        appendLoadingRef.current = true;
        setLoadingMore(true);
      } else {
        articlesRef.current = [];
        appendLoadingRef.current = false;
        setArticles([]);
        setTotal(0);
        setListLoading(true);
        setLoadingMore(false);
      }

      const seq = requestSeqRef.current + 1;
      requestSeqRef.current = seq;

      const fail = () => {
        /* 过期响应不碰任何状态，避免污染新分类 / 新一轮加载 */
        if (seq !== requestSeqRef.current) return;
        appendLoadingRef.current = false;
        setArticlesError(true);
        setHasMore(false);
        setListLoading(false);
        setLoadingMore(false);
      };

      queryNewsArticles({ category: activeCategory, page })
        .then((res) => {
          if (seq !== requestSeqRef.current) return;

          if (!res?.success || !Array.isArray(res.result?.data)) {
            fail();
            return;
          }

          const data = res.result.data.filter(Boolean);
          let nextArticles = data;

          if (append) {
            /* 后端页码越界会回落最后一页，同一篇文章可能重复下发，按 id 去重 */
            const seen = new Set(articlesRef.current.map((item) => item.id));
            nextArticles = articlesRef.current.slice();
            data.forEach((item) => {
              if (seen.has(item.id)) return;
              seen.add(item.id);
              nextArticles.push(item);
            });
          }

          const added = nextArticles.length - articlesRef.current.length;
          /* hasNext 之外再用「本页有数据且确有新增」兜底，防止重复下发导致死循环 */
          const canLoadMore =
            Boolean(res.result.hasNext) && data.length > 0 && added > 0;

          articlesRef.current = nextArticles;
          setArticles(nextArticles);
          setTotal(Number(res.result.total) || 0);
          setHasMore(canLoadMore);
          if (canLoadMore) nextPageRef.current = page + 1;

          appendLoadingRef.current = false;
          setArticlesError(false);
          setListLoading(false);
          setLoadingMore(false);
        })
        .catch(fail);
    },
    [activeCategory]
  );

  useEffect(() => {
    if (!categoriesReady) return undefined;

    nextPageRef.current = 2;
    setArticlesError(false);
    setHasMore(false);
    loadPage(1, { append: false });

    return () => {
      /* 切分类 / 重试 / 卸载：作废这条链路上的在途请求 */
      requestSeqRef.current += 1;
    };
  }, [activeCategory, categoriesReady, loadPage]);

  /** 清空分页游标（切分类 / 重新加载都走它）；页面滚动位置由页面自己处理 */
  const resetPaging = useCallback(() => {
    requestSeqRef.current += 1;
    articlesRef.current = [];
    nextPageRef.current = 2;
    appendLoadingRef.current = false;
  }, []);

  /** 续页：页面在哨兵进入视口 / 下拉越过阈值时调用 */
  const loadMore = useCallback(() => {
    loadPage(nextPageRef.current, { append: true });
  }, [loadPage]);

  const changeCategory = useCallback(
    (value) => {
      if (value === activeCategory) return;

      /* 先作废在途请求再清空，避免旧分类的续页结果落到新列表里 */
      resetPaging();

      setArticles([]);
      setTotal(0);
      setHasMore(false);
      setLoadingMore(false);
      setListLoading(true);
      setActiveCategory(value);
    },
    [activeCategory, resetPaging]
  );

  const retry = useCallback(() => {
    /* 重新加载：先重拉分类，再默认选「全部」，最后从第 1 页拉全部列表 */
    resetPaging();

    setCategoriesError(false);
    setArticlesError(false);
    setCategoryOptions([]);
    setCategoriesReady(false);
    setActiveCategory(ALL_CATEGORY.value);
    setArticles([]);
    setTotal(0);
    setHasMore(false);
    setListLoading(true);
    setLoadingMore(false);
    setRetryKey((key) => key + 1);
  }, [resetPaging]);

  return {
    activeCategory,
    articles,
    tabs,
    listLoading,
    loadingMore,
    hasMore,
    hasError,
    showPinned,
    showEmpty,
    moreSkeletonCount,
    loadMore,
    changeCategory,
    retry,
  };
}
